import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CircleAlert, CreditCard } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router";
import {
  type BillingProfileInput,
  billingProfileSchema,
  CLOSED_TEXT,
  EU_COUNTRIES,
  GUARANTEE_TEXT,
  type SubscriptionStatus,
} from "../../../shared/billing";
import { api } from "../../api/client";
import { StatusChip } from "../../components/kit";
import { Alert, ICON, SelectField, TextField } from "../../components/ui";
import { price } from "../../lib/format";
import { apiFailure, type FieldErrors, validate } from "../../pages/forms";
import type { ShellContext } from "../AppShell";

// "Abonament și facturare" (LOT 6; payments approved 2026-10-27, CERERE nr. 7). The 30 free days start with the
// restaurant, without a card; the owner enters the company's billing details, picks a plan and pays by card in
// Stripe Checkout. Every payment gets a SmartBill fiscal invoice, listed here with its link.

export interface Billing {
  status: SubscriptionStatus;
  label: string;
  trialEndsAt: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  canPublish: boolean;
  graceDays: number;
  paymentsEnabled: boolean;
  manageable: boolean;
  profile: BillingProfileInput | null;
  /** What the restaurant is charged in: lei in Romania (the amount appears at checkout), euro elsewhere in the EU. */
  payCurrency: "RON" | "EUR";
  plans: {
    id: string;
    name: string;
    interval: string;
    priceMinor: number | null;
    introPriceMinor: number | null;
    trialDays: number;
    currency: string;
  }[];
  invoices: {
    id: string;
    amountMinor: number;
    currency: string;
    status: string;
    issuedAt: number;
    fiscalNumber: string | null;
    url: string | null;
    /** The add-on „Valori nutriționale” or a credit pack; null = the subscription. */
    productKind: "addon" | "renewal" | "pack5" | "pack10" | null;
    /** Money back asked for (owner's 14-day guarantee); null when not asked. */
    refundRequestedAt: number | null;
    refundable: boolean;
  }[];
}

const day = (ms: number | null) =>
  ms ? new Date(ms).toLocaleDateString("ro-RO", { dateStyle: "long" }) : "—";
const DAY = 86_400_000;

/** "19,90 € / lună + TVA" or "99,00 € primul an + TVA, apoi 149,00 € / an + TVA", then the free days. */
function planPrice(p: Billing["plans"][number]): string {
  const cur = p.currency === "EUR" ? "EUR" : "RON";
  const unit = p.interval === "year" ? "an" : "lună";
  const main =
    p.introPriceMinor !== null && p.priceMinor !== null
      ? `${price(p.introPriceMinor, cur)} primul an + TVA, apoi ${price(p.priceMinor, cur)} / ${unit} + TVA`
      : `${price(p.priceMinor ?? 0, cur)} / ${unit} + TVA`;
  return p.trialDays > 0 ? `${main} · primele ${p.trialDays} de zile gratuite` : main;
}

const COUNTRY_NAMES: Record<(typeof EU_COUNTRIES)[number], string> = {
  RO: "România",
  AT: "Austria",
  BE: "Belgia",
  BG: "Bulgaria",
  CY: "Cipru",
  CZ: "Cehia",
  DE: "Germania",
  DK: "Danemarca",
  EE: "Estonia",
  ES: "Spania",
  FI: "Finlanda",
  FR: "Franța",
  GR: "Grecia",
  HR: "Croația",
  HU: "Ungaria",
  IE: "Irlanda",
  IT: "Italia",
  LT: "Lituania",
  LU: "Luxemburg",
  LV: "Letonia",
  MT: "Malta",
  NL: "Țările de Jos",
  PL: "Polonia",
  PT: "Portugalia",
  SE: "Suedia",
  SI: "Slovenia",
  SK: "Slovacia",
};
const COUNTRY_OPTIONS = EU_COUNTRIES.map((c) => [c, COUNTRY_NAMES[c]] as const);

const EMPTY = {
  kind: "company" as "company" | "person",
  companyName: "",
  vatCode: "",
  vatPayer: true,
  regCom: "",
  address: "",
  city: "",
  county: "",
  country: "RO",
  email: "",
};

/** The company's billing details: the SmartBill invoice and the VAT treatment come from them. */
export function ProfileForm({
  rid,
  profile,
  canEdit,
  onSaved,
}: {
  rid: string;
  profile: BillingProfileInput | null;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [v, setV] = useState({ ...EMPTY, ...profile, regCom: profile?.regCom ?? "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const save = useMutation({
    mutationFn: (input: BillingProfileInput) => api("PUT", `/restaurants/${rid}/billing/profile`, input),
    onSuccess: () => onSaved(),
    onError: (err) => {
      const f = apiFailure(err);
      setErrors(f.fields);
      setMessage(f.message);
    },
  });
  const field = (k: keyof typeof EMPTY) => ({
    id: `bp-${k}`,
    value: String(v[k] ?? ""),
    error: errors[k],
    disabled: !canEdit,
    onChange: (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value }),
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    setMessage("");
    const r = validate(billingProfileSchema, v);
    if (!r.ok) {
      setErrors(r.errors);
      return;
    }
    setErrors({});
    save.mutate(r.data);
  }
  return (
    <form onSubmit={submit} noValidate className="stack" style={{ gap: 12 }}>
      {message && (
        <Alert tone="error" live>
          {message}
        </Alert>
      )}
      <div
        className="row"
        style={{ gap: 16, flexWrap: "wrap" }}
        role="radiogroup"
        aria-label="Factura se emite pe"
      >
        {(
          [
            ["company", "Firmă"],
            ["person", "Persoană fizică"],
          ] as const
        ).map(([kind, label]) => (
          <label key={kind} className="check-row">
            <input
              type="radio"
              name="bp-kind"
              checked={v.kind === kind}
              disabled={!canEdit}
              onChange={() =>
                setV({
                  ...v,
                  kind,
                  vatCode: kind === "person" ? "" : v.vatCode,
                  vatPayer: kind === "company",
                })
              }
            />
            <span>{label}</span>
          </label>
        ))}
      </div>
      {v.kind === "person" ? (
        <TextField label="Nume și prenume" autoComplete="name" {...field("companyName")} />
      ) : (
        <>
          <TextField label="Numele firmei" autoComplete="organization" {...field("companyName")} />
          <div className="grid-2">
            <TextField label="Cod fiscal (CUI)" hint="ex. RO12345678" {...field("vatCode")} />
            <TextField label="Nr. Registrul Comerțului" hint="opțional" {...field("regCom")} />
          </div>
          <label className="check-row">
            <input
              type="checkbox"
              checked={v.vatPayer}
              disabled={!canEdit}
              onChange={(e) => setV({ ...v, vatPayer: e.target.checked })}
            />
            <span>Firma este plătitoare de TVA</span>
          </label>
          {errors.vatPayer && <span className="field-error">{errors.vatPayer}</span>}
        </>
      )}
      <TextField label="Adresa" autoComplete="street-address" {...field("address")} />
      <div className="grid-2">
        <TextField label="Localitatea" hint="în București: Sector 1 … Sector 6" {...field("city")} />
        <TextField label="Județul" {...field("county")} />
      </div>
      <div className="grid-2">
        <SelectField label="Țara" options={COUNTRY_OPTIONS} {...field("country")} />
        <TextField label="E-mail pentru facturi" type="email" autoComplete="email" {...field("email")} />
      </div>
      {canEdit && (
        <button
          className="btn btn-primary"
          type="submit"
          disabled={save.isPending}
          style={{ alignSelf: "flex-start" }}
        >
          {save.isPending ? "Se salvează…" : "Salvează datele de facturare"}
        </button>
      )}
    </form>
  );
}

export function BillingPage() {
  const { membership } = useOutletContext<ShellContext>();
  const rid = membership.restaurant.id;
  const isOwner = membership.role === "owner";
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [profileSaved, setProfileSaved] = useState(false);
  const returned = params.get("checkout");
  const billing = useQuery({
    queryKey: ["billing", rid],
    queryFn: () => api<Billing>("GET", `/restaurants/${rid}/billing`),
    // Back from Stripe: the webhook may land a few seconds after the redirect.
    refetchInterval: returned === "success" ? 4000 : false,
  });
  useEffect(() => {
    if (returned !== "success") return;
    const t = setTimeout(() => setParams({}, { replace: true }), 30_000);
    return () => clearTimeout(t);
  }, [returned, setParams]);
  const checkout = useMutation({
    mutationFn: (planId: string) =>
      api<{ url: string }>("POST", `/restaurants/${rid}/billing/checkout`, { planId }),
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });
  const portal = useMutation({
    mutationFn: () => api<{ url: string }>("POST", `/restaurants/${rid}/billing/portal`),
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });
  // Money back within REFUND_DAYS, without a reason: one click to ask, one to confirm.
  const [refundAsk, setRefundAsk] = useState<string | null>(null);
  const refund = useMutation({
    mutationFn: (invoiceId: string) =>
      api<{ requestedAt: number }>("POST", `/restaurants/${rid}/billing/invoices/${invoiceId}/refund`),
    onSuccess: () => {
      setRefundAsk(null);
      void qc.invalidateQueries({ queryKey: ["billing", rid] });
    },
  });
  const b = billing.data;
  // From the trial banner („Activează abonamentul”): bring the plans into view.
  useEffect(() => {
    if (b && window.location.hash === "#plans-h")
      document.getElementById("plans-h")?.scrollIntoView({ block: "start" });
  }, [b]);
  const now = Date.now();
  const trialLeft = b?.status === "trialing" && b.trialEndsAt ? Math.ceil((b.trialEndsAt - now) / DAY) : null;
  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <h1 className="h1">Abonament și facturare</h1>
        <p className="page-sub" style={{ margin: 0, maxWidth: 680 }}>
          Starea abonamentului, datele firmei pentru facturi și facturile emise.
        </p>
      </div>
      {returned === "success" && (
        <Alert tone="success" title="Plata a fost trimisă." live>
          Abonamentul se activează în câteva secunde, iar factura ajunge pe e-mail.
        </Alert>
      )}
      {returned === "cancel" && <Alert tone="info">Plata a fost anulată. Nu s-a încasat nimic.</Alert>}
      {billing.isError && <Alert tone="error">{billing.error.message}</Alert>}
      {b && (
        <>
          <section className="card stack" style={{ gap: 12 }} aria-labelledby="bill-h">
            <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
              <h2 id="bill-h" className="h2" style={{ margin: 0 }}>
                Abonamentul tău
              </h2>
              <StatusChip
                tone={b.canPublish ? "success" : "warning"}
                label={b.label}
                icon={b.canPublish ? Check : CircleAlert}
              />
            </div>
            {b.status === "beta" ? (
              <span className="muted" style={{ fontSize: 14 }}>
                Meniova este gratuită pentru acest restaurant: toate funcțiile sunt disponibile, fără card.
                Nimic nu se taxează automat.
              </span>
            ) : (
              <dl className="grid-2" style={{ margin: 0 }}>
                <div>
                  <dt className="caption muted">Perioada gratuită se încheie</dt>
                  <dd style={{ margin: 0 }}>
                    {day(b.trialEndsAt)}
                    {trialLeft !== null &&
                      trialLeft > 0 &&
                      ` (încă ${trialLeft} ${trialLeft === 1 ? "zi" : "zile"})`}
                  </dd>
                </div>
                <div>
                  <dt className="caption muted">
                    {b.cancelAtPeriodEnd ? "Se încheie la" : "Următoarea plată"}
                  </dt>
                  <dd style={{ margin: 0 }}>{day(b.currentPeriodEnd)}</dd>
                </div>
              </dl>
            )}
            {b.paymentsEnabled &&
              b.status === "trialing" &&
              !b.manageable &&
              trialLeft !== null &&
              trialLeft > 0 && (
                <Alert tone="info" title="Activează abonamentul acum, plătești mai târziu.">
                  Alegi planul și introduci cardul, iar prima plată se face abia pe {day(b.trialEndsAt)}, când
                  se încheie zilele gratuite. {GUARANTEE_TEXT}
                </Alert>
              )}
            {trialLeft !== null && trialLeft <= 0 && b.canPublish && (
              <Alert tone="warning">
                Perioada gratuită s-a încheiat. Meniul rămâne deschis pentru clienți încă {b.graceDays} zile
                după încheierea ei; alege un abonament ca să nu se închidă meniul și codul QR.
              </Alert>
            )}
            {!b.canPublish && <Alert tone="warning">{CLOSED_TEXT}</Alert>}
            {b.manageable && isOwner && (
              <>
                {portal.isError && (
                  <Alert tone="error" live>
                    {apiFailure(portal.error).message}
                  </Alert>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ alignSelf: "flex-start" }}
                  disabled={portal.isPending}
                  onClick={() => portal.mutate()}
                >
                  <CreditCard {...ICON} aria-hidden="true" />
                  {portal.isPending ? "Se deschide…" : "Gestionează cardul sau abonamentul"}
                </button>
              </>
            )}
          </section>
          <section className="card stack" style={{ gap: 12 }} aria-labelledby="profile-h">
            <h2 id="profile-h" className="h2">
              Date pentru facturi
            </h2>
            <span className="muted" style={{ fontSize: 14 }}>
              Factura se emite în lei, pe firmă sau pe persoană fizică din România (cu TVA). Firmele din alte
              țări UE, cu cod de TVA, plătesc în euro, cu taxare inversă; factura lor se emite la cursul BNR.
            </span>
            {profileSaved && (
              <Alert tone="success" live>
                Datele de facturare au fost salvate.
              </Alert>
            )}
            {!isOwner && (
              <span className="caption muted">
                Doar proprietarul contului poate schimba datele de facturare.
              </span>
            )}
            <ProfileForm
              key={b.profile ? JSON.stringify(b.profile) : "empty"}
              rid={rid}
              profile={b.profile}
              canEdit={isOwner}
              onSaved={() => {
                setProfileSaved(true);
                void qc.invalidateQueries({ queryKey: ["billing", rid] });
              }}
            />
          </section>
          <section className="card stack" style={{ gap: 12 }} aria-labelledby="plans-h">
            <h2 id="plans-h" className="h2">
              Planuri
            </h2>
            {checkout.isError && (
              <Alert tone="error" live>
                {apiFailure(checkout.error).message}
              </Alert>
            )}
            <ul className="verify-names">
              {b.plans.map((p) => (
                <li
                  key={p.id}
                  className="row"
                  style={{ gap: 10, flexWrap: "wrap", justifyContent: "space-between" }}
                >
                  <span>
                    <strong style={{ fontWeight: 600 }}>{p.name}</strong>{" "}
                    <span className="muted">
                      · {p.priceMinor === null ? "prețul va fi anunțat la lansare" : planPrice(p)}
                    </span>
                  </span>
                  {b.paymentsEnabled && isOwner && p.priceMinor !== null && b.status !== "active" && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={!b.profile || checkout.isPending}
                      onClick={() => checkout.mutate(p.id)}
                    >
                      <CreditCard {...ICON} aria-hidden="true" />
                      {checkout.isPending && checkout.variables === p.id
                        ? "Se deschide plata…"
                        : "Alege planul"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {b.paymentsEnabled && (
              <span className="caption muted">
                {b.payCurrency === "RON"
                  ? "Prețurile nu includ TVA. Plătești în lei: suma exactă în lei apare în pagina de plată."
                  : "Plătești în euro, fără TVA (taxare inversă)."}{" "}
                {GUARANTEE_TEXT}
              </span>
            )}
            {!b.paymentsEnabled ? (
              <span className="caption muted">
                Plățile online nu sunt disponibile momentan. Încearcă mai târziu.
              </span>
            ) : !b.profile ? (
              <span className="caption muted">Completează întâi datele pentru facturi.</span>
            ) : b.status === "trialing" ? (
              <span className="caption muted">
                Plătești cu cardul în pagina securizată Stripe. Zilele gratuite rămase se păstrează: prima
                plată se face când se încheie.
              </span>
            ) : null}
          </section>
          <section className="card stack" style={{ gap: 12 }} aria-labelledby="inv-h">
            <h2 id="inv-h" className="h2">
              Facturi
            </h2>
            {b.invoices.length ? (
              <ul className="verify-names">
                {b.invoices.map((i) => (
                  <li key={i.id}>
                    {day(i.issuedAt)} · {price(i.amountMinor, i.currency === "EUR" ? "EUR" : "RON")} ·{" "}
                    {i.status === "paid" ? "plătită" : "plată eșuată"}
                    {i.productKind &&
                      (i.productKind.startsWith("pack") ? " · credite AI" : " · Valori nutriționale")}
                    {i.url && (
                      <>
                        {" "}
                        ·{" "}
                        <a href={i.url} target="_blank" rel="noopener">
                          {i.fiscalNumber ? `Factura ${i.fiscalNumber}` : "Vezi plata"}
                        </a>
                      </>
                    )}
                    {i.refundRequestedAt !== null && (
                      <span className="muted"> · rambursare cerută pe {day(i.refundRequestedAt)}</span>
                    )}
                    {i.refundable && isOwner && refundAsk !== i.id && (
                      <>
                        {" "}
                        ·{" "}
                        <button type="button" className="btn-link" onClick={() => setRefundAsk(i.id)}>
                          Cere banii înapoi
                        </button>
                      </>
                    )}
                    {refundAsk === i.id && (
                      <div className="stack" style={{ gap: 8, marginTop: 8 }}>
                        <span style={{ fontSize: 14 }}>
                          Îți returnăm integral suma pe card, fără explicații, și închidem abonamentul: meniul
                          și codul QR se închid pentru clienți. Datele rămân salvate dacă revii.
                        </span>
                        {refund.isError && (
                          <Alert tone="error" live>
                            {apiFailure(refund.error).message}
                          </Alert>
                        )}
                        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={refund.isPending}
                            onClick={() => refund.mutate(i.id)}
                          >
                            {refund.isPending ? "Se trimite…" : "Da, cere banii înapoi"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setRefundAsk(null)}
                          >
                            Renunț
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="muted" style={{ fontSize: 14 }}>
                Nu există facturi.
              </span>
            )}
          </section>
        </>
      )}
    </>
  );
}
